/* istanbul ignore file */
import "./EditorFluent9.less";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/themes/gray.css";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import FroalaEditorComponent from "react-froala-wysiwyg";
import { ConfigurationService } from "../../../services/ConfigurationService";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

import "froala-editor/js/plugins.pkgd.min.js";

const useStyles = makeStyles({
    container: {
        minWidth: 0,
        overflowX: "visible",
        overflowY: "visible",
    },
    disabled: {
        pointerEvents: "none",
        userSelect: "none",
        opacity: 0.99,
    },
});

export interface EditorFluent9Props extends LocalizeContextProps {
    value: string | undefined;
    onRemarksUpdate: (remarks: string | undefined) => void;
    configService?: ConfigurationService;
    isFiling: boolean;
}

function replaceWithBase64(src: string, editor: any, targetImg: HTMLImageElement) {
    fetch(src)
        .then((r) => r.blob())
        .then((blob) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                editor.image.insert(e.target?.result as string, null, null, editor.image.get());
                targetImg.remove();
            };
            reader.readAsDataURL(blob);
        });
}

const EditorFluent9: React.FC<EditorFluent9Props> = (props) => {
    const [remarks, setRemarks] = useState<string>("");
    const prevValueRef = useRef<string | undefined>(undefined);
    const styles = useStyles();

    useEffect(() => {
        if (props.value !== undefined && props.value !== prevValueRef.current) {
            setRemarks(props.value);
        }
        prevValueRef.current = props.value;
    }, [props.value]);

    const onValueChanged = useCallback(
        (content: any) => {
            setRemarks(content);
            props.onRemarksUpdate(content);
        },
        [props.onRemarksUpdate]
    );

    const changeTooltipText = useCallback((commandName: string, tooltipText: string) => {
        const tooltipElements = document.querySelectorAll(`.fr-command[data-cmd="${commandName}"]`);
        tooltipElements.forEach((tooltipElement) => {
            tooltipElement.setAttribute("title", tooltipText);
        });
    }, []);

    const updateTooltips = useCallback(() => {
        const baseKey = "APP.FROALA_EDITOR.TOOLBAR.";
        const tooltips: Record<string, string> = {
            bold: "BOLD",
            italic: "ITALIC",
            underline: "UNDERLINE",
            fontSize: "FONT_SIZE",
            textColor: "TEXT_COLOR",
            backgroundColor: "TEXT_HIGHLIGHT",
            fontFamily: "FONT_FAMILY",
        };

        Object.entries(tooltips).forEach(([command, key]) => {
            changeTooltipText(command, props.translate(`${baseKey}${key}`) as string);
        });
    }, [changeTooltipText, props.translate]);

    const onEditorInitialized = useCallback(() => {
        updateTooltips();
    }, [updateTooltips]);

    return (
        <div
            data-testid="editor-fluent9"
            className={mergeClasses(
                styles.container,
                "newforma-editorFluent9Container",
                props.isFiling && styles.disabled,
                props.isFiling && "disable-editor-fluent9"
            )}
        >
            <FroalaEditorComponent
                model={remarks || props.value}
                config={{
                    toolbarButtons: [
                        "bold",
                        "italic",
                        "underline",
                        "fontSize",
                        "textColor",
                        "backgroundColor",
                        "fontFamily",
                    ],
                    key: props.configService?.froalaLicenseKey,
                    attribution: false,
                    editorClass: "newforma-froalaEditorFluent9",
                    wordCounterCount: false,
                    charCounterCount: false,
                    quickInsertEnabled: false,
                    imageEditButtons: ["btnRemove"],
                    tableEditButtons: ["btnRemove"],
                    heightMin: 50,
                    heightMax: 200,
                    theme: "gray",
                    colorsStep: 7,
                    tableDefaultWidth: "auto",
                    tableInsertHelper: false,
                    htmlUntouched: true,
                    fontSize: ["8", "10", "12", "14", "16", "18", "20", "24", "36"],
                    fontSizeDefaultSelection: "10",
                    fontSizeUnit: "pt",
                    linkEditButtons: ["btnRemove"],
                    lineBreakerTags: ["btnRemove"],
                    imageUpload: true,
                    imageUploadToBase64: false,
                    imagePaste: true,
                    events: {
                        initialized: () => {
                            onEditorInitialized();
                        },
                        "image.beforePasteUpload": function(data: any) {
                            const editor: any = this;
                            setTimeout(() => {
                                const imgs = editor.el.querySelectorAll("img");
                                const lastImg = imgs[imgs.length - 1] as HTMLImageElement;

                                if (!lastImg || !lastImg.src.startsWith("blob:")) return;

                                replaceWithBase64(lastImg.src, editor, lastImg);
                            }, 0);
                            return false;
                        },
                        "image.beforeUpload": function(files: File[]) {
                            const editor: any = this;
                            for (const file of files) {
                                const reader = new FileReader();
                                reader.onload = (e) => editor.image.insert(String(e.target?.result ?? ""));
                                reader.readAsDataURL(file);
                            }
                            return false;
                        },
                    },
                }}
                onModelChange={onValueChanged}
                tag="textarea"
            />
        </div>
    );
};

export default withLocalize(EditorFluent9);
